import React, { createContext, useContext, useState, useCallback } from 'react';
import { notifyApprovalRequest, notifyApprovalResult } from '../utils/notify';

const ApprovalContext = createContext();
const API = '/api';

export const APPROVAL_CATEGORIES = [
  { id: 'general', label: '일반', icon: '📄' },
  { id: 'hr', label: '인사', icon: '👤' },
  { id: 'admin', label: '총무', icon: '🏢' },
  { id: 'finance', label: '재무', icon: '💰' },
  { id: 'contract', label: '계약', icon: '📝' },
];

export const APPROVAL_TYPES = [
  {
    id: 'official_doc', category: 'general', label: '공문기안서', icon: '📄',
    guide: '1. 공문종류\n - 일상 공문: 경영관리본부장 전결 및 명의 발송\n - 회사의 법률적 리스크 또는 책임을 수반하는 공문: 대표이사 전결 및 명의 발송\n\n2. 작성안내\n - 파일명: 2025_공문기안서_제출처_공문제목 작성 및 첨부\n - 우편 발송의 경우 제출처(주소)를 정확하게 입력\n\n3. 결재라인\n - 기안자 → (합의)공문담당자 → PM → 본부장 → 경영관리본부장 → 대표이사',
    fields: [
      { key: 'title', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'recipient', label: '제출처', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'sendMethod', label: '제출 방법', type: 'select', required: false, options: ['--', '이메일', '우편', '팩스', '직접전달', '기타'] },
      { key: 'sendAddress', label: '제출 주소', type: 'text', required: false, placeholder: '우편 발송의 경우, 수신자 정보(이름, 연락처) 및 주소 입력 필요' },
      { key: 'sendDate', label: '공문 발송일', type: 'date', required: true },
      { key: 'content', label: '본문', type: 'richtext', required: true, placeholder: '공문 내용을 입력하세요' },
    ]
  },
  {
    id: 'work_doc', category: 'general', label: '업무기안서', icon: '📋',
    guide: '결재라인: 기안자 → 본부장',
    fields: [
      { key: 'title', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'content', label: '본문', type: 'richtext', required: true, placeholder: '업무 내용을 입력하세요' },
    ]
  },
  {
    id: 'leave', category: 'hr', label: '휴직신청서', icon: '🏖️',
    guide: '1. 휴직종류\n - 공상휴직: 업무상의 상병에 의하여 요양을 할 때\n - 사상휴직: 업무 외의 상병에 의하여 15일 이상 요양을 할 때\n - 특별휴직: 기타 직원의 휴직 신청이 정당하다고 인정되는 때\n - 가족돌봄휴직: 가족의 질병, 사고, 노령으로 인하여 가족을 돌봐야 할 때\n - 육아휴직: 자녀의 양육을 위해 사용해야 할 때\n\n2. 결재라인\n - 기안자 → (합의)인사담당자 → PM → 본부장 → 대표이사\n\n※ 첨부서류: 휴직 사유에 대한 증빙 서류 (예: 질병으로 인한 휴직인 경우 진단서 등)',
    fields: [
      { key: 'leaveType', label: '휴직 종류', type: 'select', required: true, options: ['--', '공상휴직', '사상휴직', '특별휴직', '가족돌봄휴직', '육아휴직'] },
      { key: 'startDate', label: '휴직 시작일', type: 'date', required: true, gridGroup: 'period' },
      { key: 'endDate', label: '휴직 종료일', type: 'date', required: true, gridGroup: 'period' },
      { key: 'reason', label: '휴직사유', type: 'textarea', required: true, placeholder: '필수값 입니다.' },
    ]
  },
  {
    id: 'condolence', category: 'hr', label: '경조금지급신청서', icon: '💐',
    guide: '1. 지급기준\n - 본인 신청으로 본인에게 지급되며, 경조사 발생일 이후 6개월 이내 신청해야 한다.\n - 칠순/회갑의 경우, 가족관계증명서에 기재된 주민등록번호를 기준으로 지급된다.\n\n2. 결재라인\n - 기안자 → (합의)인사담당자 → 본부장\n\n※ 첨부서류: 가족관계증명서 등',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'eventType', label: '신청 사유', type: 'select', required: true, options: ['--', '결혼', '출산', '사망(본인부모)', '사망(배우자부모)', '사망(본인형제)', '칠순/회갑', '기타'], gridGroup: 'row1' },
      { key: 'eventDate', label: '경조사 발생일', type: 'date', required: true, gridGroup: 'row1' },
      { key: 'relation', label: '신청인과의 관계', type: 'text', required: true, placeholder: '예시) 부, 모, 자녀 등', gridGroup: 'row2' },
      { key: 'targetName', label: '관계인의 성명', type: 'text', required: true, placeholder: '예시) 부,모,자녀 등', gridGroup: 'row2' },
    ]
  },
  {
    id: 'personnel', category: 'hr', label: '인사명령신청서', icon: '🔄',
    guide: '1. 작성안내\n - 인사명령은 매주 월요일 정기 공지됩니다.\n - 아래 인사명령 사유가 발생할 경우 전 주 목요일까지 인사명령 신청서를 상신해야 합니다.(완결 전 선투입 금지)\n ▶ 일상 인사명령: 근무지 이동, 수행역할 변경(경영관리본부장 전결)\n ▶ 특별 인사명령: 입사, 승진/승급/본부 이동(대표이사 전결)\n\n2. 결재라인\n - 일상 인사명령: 기안자(PM) → (합의)인사담당자 → 본부장\n - 특별 인사명령: 기안자 → (합의)인사담당자 → 본부장 → 대표이사\n\n* 인사명령 신청 대상자가 5명 이상인 경우, "1. 일반 인사명령 신청서 양식_ver 1.1" 파일 첨부 필요',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'projectName', label: '프로젝트명', type: 'text', required: false, placeholder: '계약 명과 동일하게 작성(예시: A생명 B자산운용시스템 구축)' },
      { key: 'personnelRows', label: '인사명령 대상자', type: 'personnel_table', required: true },
    ]
  },
  {
    id: 'certificate', category: 'hr', label: '제증명신청서', icon: '📜',
    guide: '1. 신청방법\n - 필요한 증명서 종류 선택 및 신청부수 기입 후 제출(중복 선택 가능)\n\n2. 결재라인\n - 1인 결재(신청 후 담당자 전달)',
    fields: [
      { key: 'certEmploy', label: '재직증명서 (부)', type: 'text', required: false, placeholder: '부수 입력', gridGroup: 'cert1' },
      { key: 'certCareer', label: '경력증명서 (부)', type: 'text', required: false, placeholder: '부수 입력', gridGroup: 'cert1' },
      { key: 'certWithhold', label: '원천징수영수증 (부)', type: 'text', required: false, placeholder: '부수', gridGroup: 'cert2' },
      { key: 'certWithholdYear', label: '귀속년도', type: 'text', required: false, placeholder: '예) 2024년', gridGroup: 'cert2' },
      { key: 'certEtc', label: '기타 서류 (부)', type: 'text', required: false, placeholder: '부수', gridGroup: 'cert3' },
      { key: 'certEtcName', label: '필요서류명', type: 'text', required: false, placeholder: '예시) 급여명세서 최근 3개월 분', gridGroup: 'cert3' },
      { key: 'purpose', label: '용도', type: 'select', required: true, options: ['--', '금융기관 제출', '관공서 제출', '취업용', '학교 제출', '기타'] },
      { key: 'submitTo', label: '제출처', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'receiveMethod', label: '수령 방법', type: 'select', required: true, options: ['이메일(스캔본)', '등기우편', '본사 직접 수령'] },
      { key: 'receiveDate', label: '수령 희망일', type: 'date', required: true },
      { key: 'remark', label: '비고', type: 'text', required: false, placeholder: '' },
    ]
  },
  {
    id: 'resignation', category: 'hr', label: '사직서', icon: '✉️',
    guide: '[안내사항]\n - 퇴직 희망 시 퇴직 행정처리 및 업무 인수인계를 위해 최소 1개월 전 소속 본부장 및 PM에게 퇴직에 대한 의사를 서면으로 통보해야 합니다.\n - 상기 내용에 따라, 사직서를 제출한 자는 퇴직 전까지 맡은 직무를 성실히 수행하고, 업무 인수인계를 완료해야 합니다.\n - 퇴직 절차를 준수하지 않고 갑작스러운 사직을 행함으로써, 발생한 손해에 대해 회사에서 손해배상 청구가 가능함을 인지하여 주시기 바랍니다.\n - 업무용 노트북은 퇴직 전 초기화 해야 하며, 본사에서 직접 확인해야 합니다.',
    fields: [
      { key: 'dept', label: '부서', type: 'text', required: true, placeholder: '소속 부서', gridGroup: 'info1' },
      { key: 'grade', label: '직급', type: 'text', required: true, placeholder: '직급', gridGroup: 'info1' },
      { key: 'joinDate', label: '입사일', type: 'date', required: true, gridGroup: 'info2' },
      { key: 'resignDate', label: '퇴사일', type: 'date', required: true, gridGroup: 'info2' },
      { key: 'pmName', label: 'PM의 성함', type: 'text', required: false, placeholder: '필수값 입니다.' },
      { key: 'preMeeting', label: '사전 면담 여부', type: 'select', required: true, options: ['본부장', 'PM'] },
      { key: 'reason', label: '사직 사유', type: 'richtext', required: true, placeholder: '사직 사유를 입력하세요' },
    ]
  },
  {
    id: 'supplies', category: 'admin', label: '물품신청서', icon: '📦',
    guide: '* 물품신청서 작성 가이드는 하단 내용 참고\n\n가. 프로젝트 물품 신청서\n1. 신청품목\n - 일반적인 신청품목은 아래 표 참고하여 작성 바랍니다.\n - 기타품목 신청시 총무 담당자에게 사전 문의하여 작성 바랍니다.\n2. 결재라인\n - 기안자 → (합의) 총무담당자 → PM → 본부장\n3. 비고\n - 물품 수령을 희망하시는 일부터 5 영업일 전에 물품신청서 상신 부탁드립니다.\n\n나. 개인 업무용 노트북 물품 신청서\n1. 결재라인\n - 기안자 → (합의) 총무담당자 → 본부장',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'projectName', label: '프로젝트 명', type: 'text', required: false, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'receiveDate', label: '인수 희망일', type: 'date', required: true, gridGroup: 'row1' },
      { key: 'receiveAddress', label: '물품 수령지 주소', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row2' },
      { key: 'receiverInfo', label: '수령인 / 연락처', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row2' },
      { key: 'remark', label: '기타 요청사항', type: 'text', required: false, placeholder: '' },
      { key: 'items', label: '신청 물품 정보', type: 'supply_table', required: true },
    ]
  },
  {
    id: 'namecard', category: 'admin', label: '명함신청서', icon: '💳',
    guide: '결재라인: 1인결재',
    fields: [
      { key: 'nameEn', label: '영문명 (*여권명과 동일하게 기재)', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'receiveDate', label: '수령 희망일', type: 'date', required: true, gridGroup: 'row1' },
      { key: 'receiveMethod', label: '수령 방법', type: 'select', required: true, options: ['본사 직접 수령', '우편 수령'], gridGroup: 'row2' },
      { key: 'receiveAddress', label: '수령 주소', type: 'text', required: false, placeholder: '필수값 입니다.', gridGroup: 'row2' },
      { key: 'remark', label: '기타 요청사항', type: 'text', required: false, placeholder: '' },
    ]
  },
  {
    id: 'security_pledge', category: 'admin', label: '정보보안및기밀유지서약서', icon: '🔒',
    guide: '결재라인: 기안자 → (합의)인사담당자 → 본부장',
    fields: [
      { key: 'clientName', label: '1. 고객사명을 기재해주세요.', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'projectName', label: '2. 프로젝트명을 기재해주세요.', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'agree3', label: '3. 프로젝트 수행 과정 중에 알 수 있는 일체의 내용 및 산출물에 대하여, 프로젝트 업무에만 사용하고 목적 외 내부 및 외부로 사용하거나 누설하지 않겠습니다.', type: 'checkbox', required: true, checkLabel: '네, 동의합니다.' },
      { key: 'agree4', label: '4. 고객사로부터 제공받은 주요 정보(서류, 사진, 영상, 전자파일, 저장매체 등)를 무단변조, 복사, 훼손, 분실 등으로부터 안전하게 관리하겠으며, 승인 받지 않은 프로그램 또는 저장매체를 고객사 내에서 사용하지 않겠습니다.', type: 'checkbox', required: true, checkLabel: '네, 동의합니다.' },
      { key: 'agree5', label: '5. 프로젝트 관련 업무를 수행함에 있어서 명백히 허가를 받은 경우 이외에는 정보나 시설 및 시스템에 접근하지 않겠으며, 지정된 데이터 처리시스템 및 시설·설비만을 이용하겠습니다.', type: 'checkbox', required: true, checkLabel: '네, 동의합니다.' },
      { key: 'agree6', label: '6. 프로젝트 종료시 고객사에서 제공 받은 고객사 소유 모든 정보 자산을 반납하겠으며, 프로젝트 관련 정보 및 고객정보, 영업비밀 등 고객사 와 수행사, 본인의 소속사에 손해가 될 수 있는 정보는 퇴직 후에도 제3자에게 누설하지 않겠습니다.', type: 'checkbox', required: true, checkLabel: '네, 동의합니다.' },
      { key: 'agree7', label: '7. 아래 상세 지침을 준수하겠습니다.\n1) 대중교통, 복도, 화장실 공개 장소에서의 통화 및 메신저등 온라인에서 프로젝트 관련 사담주의\n2) 프로젝트 문서, 회의 녹취, 이미지 유출 금지 및 프로젝트 종료시 파기\n3) 자리 공석시 책상위 및 모니터에 열린 자료 OFF\n4) 프로젝트 산출물은 필요시 고객사에 공식요청하여 승인된 목적에 따라 사용', type: 'checkbox', required: true, checkLabel: '네, 동의합니다.' },
    ]
  },
  {
    id: 'expenditure', category: 'finance', label: '지출결의서', icon: '💸',
    guide: '결재라인: 기안자 → (합의)인사담당자 → 본부장\n\n※ 첨부 자료: 지출 영수증 또는 증빙 자료',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'expenseRows', label: '지출 내역', type: 'expense_table', required: true },
      { key: 'totalAmount', label: '총 지출 합계 (원)', type: 'text', required: true, placeholder: '필수값 입니다.' },
    ]
  },
  {
    id: 'business_trip', category: 'finance', label: '출장예산신청서', icon: '✈️',
    guide: '1. 작성안내\n - 본 신청서는 최소 출장 발생 3일 전까지 상신하여야 하며, 최종 결재 완료 후 예산 반영됩니다.\n - 예산 신청 시 일시, 동반 인원, 금액 등을 자세히 작성해주시기 바랍니다.\n\n2. 결재라인\n - 기안자 → (합의)경영관리본부 → PM → 본부장 → 경영관리본부장',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'projectName', label: '프로젝트 명', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'pmName', label: 'PM 성함', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'tripStart', label: '출장 기간 시작', type: 'date', required: true, gridGroup: 'row2' },
      { key: 'tripEnd', label: '출장 기간 종료', type: 'date', required: true, gridGroup: 'row2' },
      { key: 'destination', label: '출장 지역', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row3' },
      { key: 'totalBudget', label: '총 출장 예산 (원)', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row3' },
      { key: 'cardNo', label: '법인카드 번호 (뒤 4자리)', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row4' },
      { key: 'budgetBasis', label: '금액 산출 근거', type: 'textarea', required: true, placeholder: '필수값 입니다.' },
      { key: 'remark', label: '비고', type: 'text', required: false, placeholder: '' },
      { key: 'travelerRows', label: '출장자 정보', type: 'traveler_table', required: true },
    ]
  },
  {
    id: 'budget_req', category: 'finance', label: '예산신청서', icon: '📊',
    guide: '1. 작성안내\n - 본 신청서를 상신하기 전, 본부장의 승인 득 필수\n - 추가 예산을 신청하여 사용한 모든 경비는 본부 예산에서 차감됩니다.\n - 예산은 최종 결재 완료 후 1일 이내 반영됩니다.\n\n2. 결재라인\n - 본부 내 예산: 기안자 → (합의)총무담당자 → PM → 본부장\n - 본부 외 예산: 기안자 → (합의) 총무담당자 → PM → 본부장 → 대표이사',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'projectName', label: '프로젝트 명', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'pmName', label: 'PM성함', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'preApproval', label: '본부장 사전 승인여부', type: 'select', required: true, options: ['사전 승인 받음', '사전 승인 받지 않음'] },
      { key: 'amount', label: '예산 신청금액 (원)', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row2' },
      { key: 'cardNo', label: '법인카드 번호 (마지막 4자리)', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row2' },
      { key: 'reason', label: '신청사유 (사용 목적과 금액 산출 근거를 작성해주세요)', type: 'textarea', required: true, placeholder: '사용 목적과 금액 산출 근거를 작성해주세요.' },
    ]
  },
  {
    id: 'project_contract', category: 'contract', label: '프로젝트 계약품의서', icon: '🤝',
    guide: '1. 작성 안내\n - 계약품의서 파일에 기재하여 첨부 → 문서제목: 계약품의서-프로젝트명-기안일\n\n2. 결재라인\n - 기안자(PM) → (합의)계약담당자 → 본부장 → 대표이사',
    fields: [
      { key: 'projectName', label: '프로젝트 명', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'client', label: '계약처 명', type: 'text', required: true, placeholder: '필수값 입니다.', gridGroup: 'row1' },
      { key: 'solution', label: '솔루션', type: 'select', required: false, options: ['--', 'SI', 'SM', '솔루션', '컨설팅', '기타'], gridGroup: 'row1' },
      { key: 'contractStart', label: '계약 시작일', type: 'date', required: true, gridGroup: 'row2' },
      { key: 'contractEnd', label: '계약 종료일', type: 'date', required: false, gridGroup: 'row2' },
      { key: 'contractAmount', label: '총 계약(매출) 금액 (VAT별도)', type: 'text', required: true, placeholder: '필수값 입니다. 원', gridGroup: 'row3' },
      { key: 'purchaseAmount', label: '총 매입 금액 (VAT별도)', type: 'text', required: false, placeholder: '원', gridGroup: 'row3' },
      { key: 'totalMM', label: '전체 투입인력 M/M (외주 및 매입 포함)', type: 'text', required: false, placeholder: '필수값 입니다. M/M', gridGroup: 'row4' },
      { key: 'noaMM', label: '노아 투입인력 M/M', type: 'text', required: false, placeholder: '필수값 입니다. M/M', gridGroup: 'row4' },
      { key: 'clientContact', label: '고객사 계약 담당자', type: 'text', required: false, placeholder: '예시) 총무부 김노아 대리 / 010-1234-5678 / noaats@noaats.com' },
      { key: 'attachNote', label: '첨부 파일 안내', type: 'info', value: '계약 품의서, 최종 견적서, 인력프로파일, 계약서 등을 첨부해주세요.' },
    ]
  },
  {
    id: 'project_purchase', category: 'contract', label: '프로젝트 매입계약요청서', icon: '📥',
    guide: '1. 작성안내\n - 계약 시작일 2주 전까지 첨부된 용역계약 요청서를 상신해야 합니다.(선투입 금지)\n - 모든 금액은 VAT별도 금액으로 기재하고, 계약대상자 프로파일을 첨부합니다.\n - 상세 작성 방법은 첨부된 양식 내 가이드 라인을 참고하여 주시기 바랍니다.\n\n2. 결재라인\n - 기안자(PM) → (합의)계약담당자 → 본부장 → 경영관리본부장 → 대표이사\n\n※ 첨부 파일: 매입계약요청서, 최종견적서, 인력 프로파일, 계약서 등을 첨부해주세요.',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'content', label: '본문', type: 'richtext', required: true, placeholder: '매입 계약 내용을 입력하세요' },
    ]
  },
  {
    id: 'maintenance_contract', category: 'contract', label: '유지보수 계약품의서', icon: '🔧',
    guide: '1. 작성 안내\n - 계약품의서 파일에 기재하여 첨부 → 문서제목: 유지보수 계약품의서_고객사명_계약명_기안일\n\n2. 결재라인\n - 기안자(PM) → (합의)계약담당자 → 본부장 → 대표이사\n\n※ 첨부 파일: 계약 품의서, 최종 견적서, 계약서 등을 첨부해주세요.',
    fields: [
      { key: 'contractName', label: '계약 명', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'client', label: '계약처 명', type: 'text', required: true, placeholder: '실제 계약처 명을 입력해주세요.', gridGroup: 'row1' },
      { key: 'solution', label: '솔루션', type: 'select', required: false, options: ['--', 'SI', 'SM', '솔루션', '컨설팅', '기타'], gridGroup: 'row1' },
      { key: 'contractStart', label: '계약 시작일', type: 'date', required: true, gridGroup: 'row2' },
      { key: 'contractEnd', label: '계약 종료일', type: 'date', required: false, gridGroup: 'row2' },
      { key: 'contractAmount', label: '총 계약(매출) 금액 (VAT별도)', type: 'text', required: true, placeholder: '필수값 입니다. 원', gridGroup: 'row3' },
      { key: 'purchaseAmount', label: '총 매입 금액 (VAT별도)', type: 'text', required: false, placeholder: '원', gridGroup: 'row3' },
      { key: 'totalMD', label: '총 투입공수(M/D)', type: 'text', required: true, placeholder: '필수값 입니다. M/D (*M/M 기준으로 견적서 작성 시 1M/M=22M/D로 계산, 라이선스 계약의 경우 0으로 입력)', gridGroup: 'row4' },
      { key: 'discount', label: '할인율', type: 'text', required: true, placeholder: '필수값 입니다. %', gridGroup: 'row4' },
      { key: 'cpi', label: '올해 적용된 CPI', type: 'text', required: true, placeholder: '필수값 입니다. %', gridGroup: 'row5' },
      { key: 'clientContact', label: '고객사 계약 담당자', type: 'text', required: false, placeholder: '예시) 총무부 김노아 대리 / 010-1234-5678 / noaats@noaats.com' },
      { key: 'remark', label: '비고', type: 'text', required: false, placeholder: '계약시 특이사항이 있는 경우 기재해주세요' },
    ]
  },
  {
    id: 'maintenance_purchase', category: 'contract', label: '유지보수 매입계약요청서', icon: '🛠️',
    guide: '1. 작성안내\n - 계약 시작일 2주 전까지 첨부된 용역계약 요청서를 상신해야 합니다.(선투입 금지)\n - 모든 금액은 VAT별도 금액으로 기재하고, 계약대상자 프로파일을 첨부합니다.\n - 상세 작성 방법은 첨부된 양식 내 가이드 라인을 참고하여 주시기 바랍니다.\n\n2. 결재라인\n - 기안자(PM) → (합의)계약담당자 → 본부장 → 경영관리본부장 → 대표이사\n\n※ 첨부 파일: 매입계약요청서, 최종견적서, 인력 프로파일, 계약서 등을 첨부해주세요.',
    fields: [
      { key: 'docTitle', label: '제목', type: 'text', required: true, placeholder: '필수값 입니다.' },
      { key: 'content', label: '본문', type: 'richtext', required: true, placeholder: '유지보수 매입 계약 내용을 입력하세요' },
    ]
  },
];

export function ApprovalProvider({ children }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/approvals`);
      const data = await res.json();
      setApprovals(data);
    } catch (e) {
      console.error('결재 목록 조회 실패:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const addApproval = async (data) => {
    try {
      const res = await fetch(`${API}/approvals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const newApproval = await res.json();
      setApprovals(prev => [newApproval, ...prev]);
      if (data.approvers?.length > 0) {
        notifyApprovalRequest({ approverName: data.approvers[0].name, docTitle: data.title, requesterName: data.author.name });
      }
      return newApproval;
    } catch (e) {
      console.error('결재 생성 실패:', e);
    }
  };

  const updateApproval = async (id, approverId, action, comment) => {
    try {
      const res = await fetch(`${API}/approvals/${id}/action`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approverId, action, comment }),
      });
      const updated = await res.json();
      setApprovals(prev => prev.map(a => a.id === id ? updated : a));
      if (updated.status !== 'pending') {
        notifyApprovalResult({ requesterName: updated.author.name, docTitle: updated.title, status: updated.status });
      }
      return updated;
    } catch (e) {
      console.error('결재 처리 실패:', e);
    }
  };

  const getMyApprovals = (userId) => approvals.filter(a => a.author.id === userId);
  const getPendingForMe = (userId) => approvals.filter(a =>
    a.status === 'pending' && a.approvers[a.currentStep]?.id === userId
  );

  return (
    <ApprovalContext.Provider value={{ approvals, loading, fetchApprovals, addApproval, updateApproval, getMyApprovals, getPendingForMe, APPROVAL_TYPES, APPROVAL_CATEGORIES }}>
      {children}
    </ApprovalContext.Provider>
  );
}

export const useApproval = () => useContext(ApprovalContext);
